import React, { memo, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Camera, Canvas } from "@react-three/fiber";
import * as THREE from "three";
import { Color, Vector3 } from "three";
import { CAMERA_FOV, COLORS } from "../../constants";
import { Lights } from "../Lights";
import { useFBX, useGLTF, useProgress } from "@react-three/drei";
import { useSafeGLTF } from "../../utils/safeGLTFLoader";
import { GLTFResult, ImportedRoom } from "../SceneObjects/room/ImportedRoom";
import { SectionSizes } from "../SceneObjects/SectionLabels/SectionSizes";
import { SectionButtons } from "../SceneObjects/SectionLabels/SectionButtons";
import { SmartOrbitControls } from "../SmartOrbitControls/SmartOrbitControls";
import { Alert, Stack } from "@mui/material";
import { useAlerts } from "../../hooks/useAlerts";
import { useCameraCustom } from "../../hooks/useCameraCustom";
import { Racks } from "../SceneObjects/sections/Racks";
import shallow from "zustand/shallow";
import { useCurrent } from "../../hooks/useCurrent";
import { EditItemButtons } from "../SceneObjects/SectionLabels/EditItemButtons";
import { CustomOutline } from "../Effects/CustomOutline";
import { SceneProvider } from "../Context/SceneContext";
import { CanvasSize } from "./CanvasSize";
import DecorsList from "../SceneObjects/DecorsList/DecorsList";
import { useStoreSystem } from "../../store";
import { useShelvingStore } from "../../store/useShelvingStore";
import { useRoomSizes } from "../../hooks";
import { DecorsLabels } from "../SceneObjects/SectionLabels/DecorsLabels";
import { getCameraPositionToMiddleLine, getTargetPositionToMiddleLine } from "../../store/helpers";
import { WriteMainScene } from "./WriteMainScene";
import TireRacksList from "../SceneObjects/TireRacksList/TireRacksList";
import { TireRacksLabels } from "../SceneObjects/SectionLabels/TireRacksLabels";
import WallCenterSpheres from "../SceneObjects/WallCenterSpheres/WallCenterSpheres";

export const MainCanvas = memo(() => {
    const { location, updateLabels, targetZoom, itemsGroup, currentSide, isPdfPreparation } = useStoreSystem(
        (state) => ({
            location: state.location,
            updateLabels: state.updateLabels,
            targetZoom: state.targetZoom,
            itemsGroup: state.itemsGroup,
            updateItemsGroup: state.updateItemsGroup,
            currentSide: state.currentSide,
            isPdfPreparation: state.isPdfPreparation,
        }),
        shallow
    );

    const { originDecors, setSystemProperties } = useStoreSystem(
        (state) => ({
            originDecors: state.originDecors,
            setSystemProperties: state.setSystemProperties,
        }),
        shallow
    );
    const { constructions } = useStoreSystem(
        (state) => ({
            constructions: state.constructions,
        }),
        shallow
    );
    const { getCurrentShelf, colorsItems, setColorsItems } = useShelvingStore(
        (state) => ({
            getCurrentShelf: state.getCurrentShelving,
            colorsItems: state.colorsItems,
            setColorsItems: state.setColorsItems,
        }),
        shallow
    );

    const currentShelf = getCurrentShelf();
    const { roomSizes, roomHeight, roomLength } = useRoomSizes();

    const totalHeight = currentShelf.height;
    const totalLength = currentShelf.length;
    const sideIndent = currentShelf.sideIndent;

    const { alerts } = useAlerts();
    const { isMoveCamera, setMoveCameraEnabled, setIsMoveCamera } = useCameraCustom();
    const isMoveCameraRef = useRef<boolean>(false);
    const moveCameraEnabled = useRef<boolean>(true);
    const sections = currentShelf.sections;

    const { setCurrentEditItem, setCurrentSectionId, currentEditItem } = useCurrent();

    const { progress } = useProgress();

    const [canvasSize, setCanvasSize] = useState<any>(null);
    const [sceneCamera, setSceneCamera] = useState<Camera | null>(null);

    const canvasRef = useRef<HTMLCanvasElement>(null);

    const handleCanvasCreate = useCallback(
        ({ camera, scene }: { camera: THREE.OrthographicCamera | THREE.PerspectiveCamera; scene: THREE.Scene }) => {
            scene.background = new Color(COLORS.sceneBackground);
            camera.position.set(0, 1.3, 5.8);
            if ("fov" in camera) camera.fov = CAMERA_FOV;
            camera.updateProjectionMatrix();
            setSceneCamera(camera);
        },
        []
    );

    const gltfRoom = useSafeGLTF(`${process.env.PUBLIC_URL}/room.glb?v=1`) as GLTFResult;

    useEffect(() => {
        if (itemsGroup.length === 0 && originDecors.length === 0) return;

        useGLTF.preload(`${process.env.PUBLIC_URL}/room.glb?v=1`);

        itemsGroup.forEach((group) => {
            group?.ELEMENTS?.forEach((element) => {
                useGLTF.preload(element.PROPERTIES.FILE_3D);
            });
        });
        originDecors.forEach((originDecor) => {
            if (/\.(gltf|glb)$/i.test(originDecor.PROPERTIES.FILE_3D)) {
                useGLTF.preload(originDecor.PROPERTIES.FILE_3D);
            } else {
                useFBX.preload(originDecor.PROPERTIES.FILE_3D);
            }
        });
        Object.values(constructions).forEach((category) => {
            category.ELEMENTS?.forEach((element) => {
                if (element.PROPERTIES.FILE_3D) {
                    useGLTF.preload(element.PROPERTIES.FILE_3D);
                }
            });
        });
    }, [itemsGroup, originDecors, constructions]);

    useEffect(() => {
        setMoveCameraEnabled(true);
        setIsMoveCamera(false);
    }, [canvasSize]);

    useEffect(() => {
        setColorsItems(colorsItems);
    }, []);

    const handleOrbitControlsChange = useCallback(
        (e: any) => {
            updateLabels();
        },
        [updateLabels]
    );

    const setCameraToMiddleLine = (lineVertices: Vector3[]) => {
        setSystemProperties([
            { name: "cameraPosition", value: getCameraPositionToMiddleLine(lineVertices) },
            { name: "targetCameraPosition", value: getTargetPositionToMiddleLine(lineVertices) },
        ]);
    };

    useEffect(() => {
        if (targetZoom === "item" && currentEditItem.id) {
            for (let q = 0; q < sections.length; q++) {
                const currentItem = sections[q].items.find((item) => item.id === currentEditItem.id);
                if (currentItem) {
                    const position = currentItem.position;
                    const startXPoint = -roomSizes?.width / 2000 + sideIndent / 2000;
                    const lineVertices = [
                        new Vector3(startXPoint + position[0], position[1] + currentItem.height / 2000, position[2]),
                        new Vector3(startXPoint + position[0], position[1] + currentItem.height / 2000, position[2]),
                    ];
                    setSystemProperties([
                        { name: "targetCameraPosition", value: getTargetPositionToMiddleLine(lineVertices) },
                    ]);
                    break;
                }
            }
        }
    }, [targetZoom, currentEditItem.id]);

    const onResizeCanvasHandler = useCallback(
        (size: any) => {
            setCanvasSize(size);
        },
        [setCanvasSize]
    );

    useEffect(() => {
        !isMoveCamera && updateLabels();
    }, [sections, totalHeight, totalLength, isMoveCamera]);

    return (
        <>
            <Canvas
                ref={canvasRef}
                gl={{
                    preserveDrawingBuffer: true,
                    localClippingEnabled: true,
                    outputEncoding: THREE.sRGBEncoding,
                    toneMapping: THREE.ACESFilmicToneMapping,
                }}
                resize={{
                    scroll: false,
                    debounce: { scroll: 50, resize: 0 },
                }}
                onCreated={handleCanvasCreate}
                tabIndex={0}
                onPointerMissed={() => {
                    setCurrentEditItem(undefined, false);
                    setCurrentSectionId(undefined);
                }}
            >
                <SmartOrbitControls
                    moveCameraEnabled={moveCameraEnabled.current}
                    onChange={handleOrbitControlsChange}
                    makeDefault
                    enabled={true}
                    isMoveCameraRef={isMoveCameraRef}
                    roomHeight={roomHeight / 1000}
                    roomLength={roomLength / 1000}
                    roomSizes={roomSizes}
                    locationSubMenu={""}
                    setIsMoveCamera={setIsMoveCamera}
                    currentSide={currentSide}
                />
                <Lights />
                <WriteMainScene />
                <CanvasSize onResize={onResizeCanvasHandler} />
                <SceneProvider>
                    <Suspense fallback={null}>
                        <ImportedRoom gltfRoom={gltfRoom} />
                        {(currentSide !== "back" || isPdfPreparation) && (
                            <Racks side="front" moveCameraEnabled={moveCameraEnabled} />
                        )}
                        {(currentSide !== "right" || isPdfPreparation) && (
                            <Racks side="left" moveCameraEnabled={moveCameraEnabled} />
                        )}
                        {(currentSide !== "left" || isPdfPreparation) && (
                            <Racks side="right" moveCameraEnabled={moveCameraEnabled} />
                        )}
                        {(currentSide !== "front" || isPdfPreparation) && (
                            <Racks side="back" moveCameraEnabled={moveCameraEnabled} />
                        )}
                        <DecorsList moveCameraEnabled={moveCameraEnabled} gltfRoom={gltfRoom} />
                        <TireRacksList moveCameraEnabled={moveCameraEnabled} />
                        {/* <WallCenterSpheres /> */}
                    </Suspense>
                    <CustomOutline />
                </SceneProvider>
            </Canvas>
            {progress === 100 && !isMoveCamera && (
                <>
                    <SectionSizes camera={sceneCamera} canvasSize={canvasSize} />
                    {(location === "sections" || location === "items") && (
                        <SectionButtons camera={sceneCamera} canvasSize={canvasSize} location={location} />
                    )}
                    {location === "items" && <EditItemButtons camera={sceneCamera} canvasSize={canvasSize} />}
                    {/*TODO разобрать с этим компонентом */}
                    {sceneCamera && <DecorsLabels camera={sceneCamera} canvasSize={canvasSize} />}
                    {sceneCamera && <TireRacksLabels camera={sceneCamera} canvasSize={canvasSize} />}
                </>
            )}

            {alerts.length > 0 && (
                <Stack spacing={0.5} position={"absolute"} right={10} zIndex={9999} top={10} width={500}>
                    {alerts.map((alert) => (
                        <Alert key={alert.id} severity={alert.severity}>
                            {alert.text}
                        </Alert>
                    ))}
                </Stack>
            )}
        </>
    );
});
